import { useState, useEffect, useRef, useCallback } from "react";
import { get, ref } from "firebase/database";
import { db } from "../firebase/config";

// ─────────────────────────────────────────────────────────────
// usePolling
//
// A fallback polling hook in case the Firebase realtime listener
// drops (e.g. flaky network, WebSocket blocked).
//
// How it works:
//   - Polls Firebase every `interval` ms
//   - Only activates if `active` is true
//   - Automatically pauses when the tab is hidden (saves quota)
//   - Exposes connection status so UI can show a warning
//
// Usage:
//   const { polledData, isConnected } = usePolling(roomCode, 2000);
// ─────────────────────────────────────────────────────────────

const usePolling = (roomCode, interval = 2000, active = true) => {
  const [polledData, setPolledData] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const [failCount, setFailCount]     = useState(0);

  const intervalRef    = useRef(null);
  const isVisibleRef   = useRef(true);   // Page Visibility API
  const isMountedRef   = useRef(true);

  // ── Poll function ──────────────────────────────────────────
  const poll = useCallback(async () => {
    if (!roomCode || !isVisibleRef.current) return;

    try {
      const snapshot = await get(ref(db, `rooms/${roomCode}`));

      if (!isMountedRef.current) return;

      if (snapshot.exists()) {
        setPolledData(snapshot.val());
        setIsConnected(true);
        setFailCount(0);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      console.warn("[usePolling] Poll failed:", err.message);
      setFailCount(prev => {
        const next = prev + 1;
        // After 3 consecutive failures → mark as disconnected
        if (next >= 3) setIsConnected(false);
        return next;
      });
    }
  }, [roomCode]);

  // ── Page Visibility — pause polling when tab is hidden ─────
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === "visible";
      // Resume immediately when tab becomes visible again
      if (isVisibleRef.current) poll();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [poll]);

  // ── Start / stop polling ───────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;

    if (!active || !roomCode) return;

    // Poll once immediately
    poll();

    // Then poll on interval
    intervalRef.current = setInterval(poll, interval);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, roomCode, interval, poll]);

  // ── Manual trigger ─────────────────────────────────────────
  const pollNow = useCallback(() => {
    poll();
  }, [poll]);

  return {
    polledData,   // Latest room data from polling
    isConnected,  // false after 3+ consecutive failures
    failCount,    // How many times polling has failed in a row
    pollNow,      // Call this to force an immediate poll
  };
};

export default usePolling;